import { brandingSchema, maxBrandingImageBytes, type BrandingAssetKind, type BrandingInput, type BrandingSettings } from '@smarton-pbx/shared';
import { ImageIcon, Trash2, Upload } from 'lucide-react';
import { type CSSProperties, type FormEvent, useState } from 'react';
import { t } from '../i18n';
import { readableTextOn } from '../lib/branding';
import { useSubmit } from '../lib/hooks';
import { Field, Notice } from './ui';

const hex = /^#[0-9a-fA-F]{6}$/;

/** Name, colours and images, with a live preview; for tenant admins and the platform console. */
export function BrandingForm({
  branding,
  imageUrl,
  save,
  uploadImage,
  removeImage,
}: {
  branding: BrandingSettings;
  imageUrl: (kind: BrandingAssetKind, version: string) => string;
  save: (input: BrandingInput) => Promise<unknown>;
  uploadImage: (kind: BrandingAssetKind, file: File) => Promise<unknown>;
  removeImage?: (kind: BrandingAssetKind) => Promise<unknown>;
}) {
  const [productName, setProductName] = useState(branding.productName);
  const [primaryColor, setPrimaryColor] = useState(branding.primaryColor);
  const [accentColor, setAccentColor] = useState(branding.accentColor);
  const [emailSenderName, setEmailSenderName] = useState(branding.emailSenderName);
  const [saved, setSaved] = useState(false);
  const [imageError, setImageError] = useState<string>();
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    if (await form.submit(brandingSchema, { productName, primaryColor, accentColor, emailSenderName }, async (v) => (await save(v), true))) {
      setSaved(true);
    }
  }

  async function upload(kind: BrandingAssetKind, file: File | undefined) {
    setImageError(undefined);
    if (!file) return;
    if (file.size > maxBrandingImageBytes) {
      setImageError(t('Images can be up to 512 KB.'));
      return;
    }
    try {
      await uploadImage(kind, file);
    } catch (err) {
      setImageError(err instanceof Error ? err.message : String(err));
    }
  }

  const previewColor = hex.test(primaryColor) ? primaryColor : branding.primaryColor;
  const previewStyle = { '--brand': previewColor, '--brand-text': readableTextOn(previewColor) } as CSSProperties;

  const image = (kind: BrandingAssetKind, label: string, hint: string, version: string | null) => (
    <div className="image-tile">
      <div className="spread">
        <div>
          <h3>{label}</h3>
          <div className="subtle">{hint}</div>
        </div>
      </div>
      <div className="image-box">{version ? <img src={imageUrl(kind, version)} alt="" /> : <ImageIcon size={22} className="subtle" aria-hidden />}</div>
      <div className="row">
        <label className="button secondary small">
          <Upload size={15} aria-hidden /> {version ? t('Replace') : t('Upload')}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon"
            hidden
            onChange={(e) => void upload(kind, e.target.files?.[0])}
          />
        </label>
        {version && removeImage && (
          <button type="button" className="ghost small" onClick={() => void removeImage(kind)}>
            <Trash2 size={15} aria-hidden /> {t('Remove')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="stack-lg">
      <div className="branding-layout">
        <form className="card" onSubmit={submit} noValidate>
          <div className="card-body stack">
            <div className="form-grid">
              <Field label={t('Product name')} hint={t('What users see, instead of ours')} error={form.fields.productName}>
                <input value={productName} onChange={(e) => setProductName(e.target.value)} data-testid="product-name" />
              </Field>
              <Field label={t('Email sender name')} hint={t('Shown as the sender of emails')} error={form.fields.emailSenderName}>
                <input value={emailSenderName} onChange={(e) => setEmailSenderName(e.target.value)} />
              </Field>
              <Field label={t('Main colour')} hint={t('Buttons, links, highlights')} error={form.fields.primaryColor}>
                <span className="color-input">
                  <input type="color" value={previewColor} onChange={(e) => setPrimaryColor(e.target.value)} aria-label={t('Pick the main colour')} />
                  <input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} aria-label={t('Main colour')} />
                </span>
              </Field>
              <Field label={t('Accent colour')} hint={t('Used sparingly')} error={form.fields.accentColor}>
                <span className="color-input">
                  <input
                    type="color"
                    value={hex.test(accentColor) ? accentColor : branding.accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    aria-label={t('Pick the accent colour')}
                  />
                  <input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} aria-label={t('Accent colour')} />
                </span>
              </Field>
            </div>
            {form.error && <Notice kind="error">{form.error}</Notice>}
            {saved && <Notice kind="success">{t('Saved. Everyone sees the new branding the next time a page loads.')}</Notice>}
          </div>
          <div className="card-footer">
            <button type="submit" disabled={form.busy} data-testid="save-branding">
              {t('Save branding')}
            </button>
          </div>
        </form>

        <div className="card preview" style={previewStyle} aria-label={t('Preview')}>
          <div className="spread">
            <h3>{t('Preview')}</h3>
            <span className="subtle">{t('Live')}</span>
          </div>
          <div className="preview-app" aria-hidden>
            <div className="side">
              <div className="row" style={{ gap: 6, marginBottom: 6 }}>
                <span className="monogram" style={{ width: 18, height: 18, fontSize: 10, borderRadius: 5 }}>
                  {productName.trim()[0]?.toUpperCase()}
                </span>
              </div>
              <div className="item active" />
              <div className="item" />
              <div className="item" />
            </div>
            <div className="content">
              <div className="bar" style={{ width: '55%', height: 12 }} />
              <div className="bar" style={{ width: '85%' }} />
              <div className="bar" style={{ width: '70%' }} />
              <div className="btn" />
            </div>
          </div>
          <button type="button" tabIndex={-1} style={{ pointerEvents: 'none' }}>
            {productName || t('Product name')}
          </button>
        </div>
      </div>

      <div className="image-tiles">
        {image('logo', t('Logo'), t('Shown in the sidebar and on the sign-in page'), branding.logoVersion)}
        {image('favicon', t('Browser tab icon'), t('A small square image'), branding.faviconVersion)}
      </div>
      {imageError && <Notice kind="error">{imageError}</Notice>}
    </div>
  );
}
